/**
 * MSAL認証コンテキスト
 *
 * Azure ADのログイン状態を管理するコンテキスト。
 * 既存のAuthContextと並行して使用可能。
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { InteractionRequiredAuthError, InteractionStatus } from '@azure/msal-browser';
import { loginRequest, isMsalConfigured } from '../services/msalConfig';
import { setM365TokenProvider } from '../services/m365Api';

const MsalAuthContext = createContext(null);

/**
 * MSAL認証プロバイダー
 */
export const MsalAuthProvider = ({ children }) => {
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [tokenReady, setTokenReady] = useState(false);
  const recoveringRef = useRef(false);

  // 現在のアカウント
  const account = accounts[0] || null;

  /**
   * アクセストークンを取得（自動リカバリー付き）
   *
   * 3段階のフォールバックでトークンを取得:
   * 1. acquireTokenSilent: MSALキャッシュ + リフレッシュトークン（~7日有効）
   * 2. ssoSilent: ブラウザのMSセッションCookieを利用（iframe）
   * 3. loginRedirect: MSログインページにリダイレクト（自動サインイン）
   *
   * ユーザーがブラウザでMSアカウントにログイン済みなら、
   * 手動操作なしで永続的にセッションが維持される。
   */
  const acquireTokenWithRecovery = useCallback(async () => {
    if (!account) return null;

    // 1. キャッシュ / リフレッシュトークンで取得（通常はこれで成功）
    try {
      const response = await instance.acquireTokenSilent({
        ...loginRequest,
        account: account,
      });
      return response.idToken;
    } catch (error) {
      // InteractionRequiredAuthError 以外のエラー（キャッシュ切れ、ネットワーク障害等）も
      // リカバリーフロー（ssoSilent → loginRedirect）に進む
      if (!(error instanceof InteractionRequiredAuthError)) {
        console.warn('Token acquisition error (will attempt recovery):', error.message);
      }
    }

    // リフレッシュトークン期限切れ → リカバリー開始
    // 同時リカバリーを防止（複数リクエストが同時にここに来る場合）
    if (recoveringRef.current) return null;
    recoveringRef.current = true;

    // 2. ssoSilent: ブラウザのMSセッションを利用（ページ遷移なし）
    try {
      const ssoResponse = await instance.ssoSilent({
        ...loginRequest,
        loginHint: account.username,
      });
      recoveringRef.current = false;
      return ssoResponse.idToken;
    } catch (ssoError) {
      console.warn('ssoSilent failed, will redirect to Microsoft login');
    }

    // 3. loginRedirect: MSログインページにリダイレクト
    //    ユーザーがMSにログイン済みなら自動でサインインされ、
    //    ページに戻ってくる。手動操作は不要。
    recoveringRef.current = false;
    setSessionExpired(true);
    return null;
  }, [instance, account]);

  /**
   * セッション期限切れ時に自動でloginRedirectを実行
   * ページがMSログインに遷移し、自動サインイン後に戻ってくる。
   */
  useEffect(() => {
    if (sessionExpired && inProgress === InteractionStatus.None) {
      instance.loginRedirect(loginRequest).catch((err) => {
        console.error('Auto redirect login failed:', err);
      });
    }
  }, [sessionExpired, inProgress, instance]);

  /**
   * ユーザー情報を更新し、トークンプロバイダーを登録
   */
  useEffect(() => {
    if (isAuthenticated && account) {
      setUser({
        email: account.username,
        name: account.name || account.username,
        tenantId: account.tenantId,
        localAccountId: account.localAccountId,
      });
      setM365TokenProvider(() => acquireTokenWithRecovery());
      setTokenReady(true);
      setSessionExpired(false);
    } else {
      setUser(null);
      setM365TokenProvider(null);
      setTokenReady(false);
    }
  }, [isAuthenticated, account, acquireTokenWithRecovery]);

  /**
   * 初期化完了を検知
   */
  useEffect(() => {
    if (inProgress === InteractionStatus.None) {
      setLoading(false);
    }
  }, [inProgress]);

  /**
   * ログイン（ポップアップ）
   */
  const login = useCallback(async () => {
    setError(null);
    try {
      await instance.loginPopup(loginRequest);
    } catch (err) {
      console.error('Login failed:', err);
      setError('ログインに失敗しました');
      throw err;
    }
  }, [instance]);

  /**
   * ログイン（リダイレクト）
   */
  const loginRedirect = useCallback(async () => {
    setError(null);
    try {
      await instance.loginRedirect(loginRequest);
    } catch (err) {
      console.error('Login redirect failed:', err);
      setError('ログインに失敗しました');
      throw err;
    }
  }, [instance]);

  /**
   * ログアウト
   */
  const logout = useCallback(async () => {
    setM365TokenProvider(null);
    try {
      await instance.logoutPopup();
    } catch (err) {
      console.error('Logout failed:', err);
      // ポップアップが失敗した場合はリダイレクトを試行
      await instance.logoutRedirect();
    }
  }, [instance]);

  const value = {
    isAuthenticated,
    user,
    loading,
    error,
    sessionExpired,
    tokenReady,
    inProgress: inProgress !== InteractionStatus.None,
    login,
    loginRedirect,
    logout,
    isConfigured: isMsalConfigured(),
  };

  return (
    <MsalAuthContext.Provider value={value}>
      {children}
    </MsalAuthContext.Provider>
  );
};

/**
 * MSAL認証フック
 */
export const useMsalAuth = () => {
  const context = useContext(MsalAuthContext);
  if (!context) {
    throw new Error('useMsalAuth must be used within a MsalAuthProvider');
  }
  return context;
};

export default MsalAuthContext;

